import { Box, Flex, Text, chakra } from "@chakra-ui/react";
import { MatchesPageSpark } from "@greenflagdate/shared";
import { BottomDrawer } from "../BottomDrawer/BottomDrawer";
import { Icon } from "../Icon/Icon";
import { InviteSparkFlow } from "../InviteSparkFlow/InviteSparkFlow";
import { Avatar } from "../InviteSparkFlow/InviteSparkPrimitives";
import { primaryProfileImagePath } from "../../lib/profileMedia";

function ActionRow({
  label,
  onClick,
  isFirst,
}: {
  label: string;
  onClick: () => void;
  isFirst?: boolean;
}) {
  return (
    <chakra.button
      type="button"
      onClick={onClick}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap="0.75rem"
      w="auto"
      mx="-1rem"
      textAlign="left"
      bg="transparent"
      border="0"
      borderTop={isFirst ? "1px solid" : undefined}
      borderBottom="1px solid"
      borderColor="border.subtle"
      px="1.25rem"
      py="0.875rem"
      cursor="pointer"
      _hover={{ bg: "bg.subtle" }}
    >
      <Text fontSize="md" fontWeight="semibold" color="fg.default">
        {label}
      </Text>
      <Icon name="chevron-right" boxSize="12px" color="fg.muted" />
    </chakra.button>
  );
}

export type SparkActionsDrawerProps = {
  spark: MatchesPageSpark | null;
  onClose: () => void;
  onViewEvent: (spark: MatchesPageSpark) => void;
  onViewProfile: (spark: MatchesPageSpark) => void;
  /** Route the viewer to an event's page to register before inviting. */
  onRegisterForEvent: (sparkId: number) => void;
  /** Route the viewer to the invitee's own event to register and JOIN them
   * (no invite — they're already going). */
  onJoinSparkEvent: (sparkId: number) => void;
  /** Refresh matches data after an invite is sent (so status updates). */
  onInvited: () => void;
};

export function SparkActionsDrawer({
  spark,
  onClose,
  onViewEvent,
  onViewProfile,
  onRegisterForEvent,
  onJoinSparkEvent,
  onInvited,
}: SparkActionsDrawerProps) {
  const firstName = spark?.profile.firstName || "";

  return (
    <BottomDrawer open={!!spark} onClose={onClose}>
      {spark && (
        <Flex direction="column" gap="1rem">
          <chakra.button
            type="button"
            onClick={() => onViewProfile(spark)}
            display="flex"
            alignItems="center"
            gap="0.875rem"
            w="auto"
            mx="-0.5rem"
            px="0.5rem"
            py="0.375rem"
            borderRadius="md"
            textAlign="left"
            bg="transparent"
            border="0"
            cursor="pointer"
            // No background hover — it bled under the drawer's close (X) button.
            // Bold the "View profile" link instead when the row is hovered.
            _hover={{ "& [data-view-profile]": { fontWeight: "bold" } }}
          >
            <Avatar
              path={primaryProfileImagePath(spark.profile)}
              alt={firstName}
              size="64px"
            />
            <Box minW="0">
              <Text fontSize="lg" fontWeight="semibold" truncate>
                {firstName}
                {spark.profile.age ? `, ${spark.profile.age}` : ""}
              </Text>
              <Text fontSize="sm" color="fg.muted">
                Mutual Spark
              </Text>
              <Text
                as="span"
                data-view-profile
                display="inline-block"
                mt="0.25rem"
                fontSize="sm"
                fontWeight="semibold"
                color="fg.brand"
                textDecoration="underline"
              >
                View profile
              </Text>
            </Box>
          </chakra.button>

          {spark.upcomingEvent && (
            <Flex direction="column">
              <ActionRow
                label="View their event"
                onClick={() => onViewEvent(spark)}
                isFirst
              />
            </Flex>
          )}

          <Box borderTop="1px solid" borderColor="border.subtle" pt="1rem">
            <InviteSparkFlow
              spark={spark}
              onDone={onClose}
              onInvited={onInvited}
              onRegisterForEvent={onRegisterForEvent}
              onJoinSparkEvent={onJoinSparkEvent}
              hideHeaderIdentity
            />
          </Box>
        </Flex>
      )}
    </BottomDrawer>
  );
}
